import {
  ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS,
  EstablishedAhpSessionResumptionTimeoutError,
  type EstablishedAhpResumptionAttempt,
  type EstablishedAhpResumptionPhase,
} from './contracts'
import type {PageActivity} from '@github-ui/ahp-relay/page-activity'

function abortReason(signal: AbortSignal): Error {
  return signal.reason instanceof Error
    ? signal.reason
    : new EstablishedAhpSessionResumptionTimeoutError(ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS)
}

export function createEstablishedAhpResumptionDeadline(
  timeoutMs = ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS,
  options?: {activity?: PageActivity},
): EstablishedAhpResumptionAttempt {
  const activity = options?.activity
  const controller = new AbortController()
  let phase: EstablishedAhpResumptionPhase = 'initial'
  let monotonicExpiresAt = performance.now() + timeoutMs
  let wallOffset = Date.now() - performance.now()
  let frozenExpiresAt: number | undefined
  let activeElapsedMs = 0
  let activeSince = activity?.isActive() ? performance.now() : undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  let disposed = false
  let unsubscribeActivity: (() => void) | undefined
  // Map both monotonic budgets to wall-clock epoch ms for consumers comparing dataUpdatedAt against expiresAt.
  // While paused, project expiry from the remaining active time; freeze the cutoff on abort or disposal.
  const remainingMs = () => {
    const now = performance.now()
    const remaining = activity
      ? timeoutMs - (activeElapsedMs + (activeSince !== undefined ? now - activeSince : 0))
      : monotonicExpiresAt - now
    if (remaining > 0) {
      if (activity) monotonicExpiresAt = now + remaining
      wallOffset = Date.now() - now
    }
    return Math.max(0, remaining)
  }
  const currentExpiresAt = () => {
    remainingMs()
    return monotonicExpiresAt + wallOffset
  }
  const freezeExpiresAt = () => {
    frozenExpiresAt ??= currentExpiresAt()
  }
  controller.signal.addEventListener('abort', freezeExpiresAt, {once: true})
  const clearTimer = () => {
    if (timer) clearTimeout(timer)
    timer = undefined
  }
  const cleanup = () => {
    clearTimer()
    unsubscribeActivity?.()
    unsubscribeActivity = undefined
  }
  const onActivityChange = () => {
    if (disposed || controller.signal.aborted || !activity) return
    const remaining = remainingMs()
    if (!activity.isActive()) {
      if (remaining === 0) {
        controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs))
        return
      }
      if (activeSince !== undefined) activeElapsedMs += performance.now() - activeSince
      activeSince = undefined
      clearTimer()
    } else if (activeSince === undefined) {
      activeSince = performance.now()
      arm()
    }
  }
  const expireFromTimer = () => {
    // At or before expiry, no awake time has passed after the cutoff, so the current offset includes sleep.
    // After expiry, keep the last live offset so a later sleep cannot admit late data.
    if (performance.now() <= monotonicExpiresAt) wallOffset = Date.now() - performance.now()
    controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs))
  }
  const arm = () => {
    if (disposed || controller.signal.aborted) return
    if (activity && !unsubscribeActivity) unsubscribeActivity = activity.subscribe(onActivityChange)
    if (activity && !activity.isActive()) {
      onActivityChange()
      return
    }
    const remaining = remainingMs()
    if (remaining === 0) {
      clearTimer()
      controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs))
      return
    }
    if (timer) return
    timer = setTimeout(() => {
      if (!activity) {
        expireFromTimer()
        return
      }
      timer = undefined
      if (!activity.isActive()) {
        onActivityChange()
        return
      }
      if (remainingMs() > 0) {
        arm()
        return
      }
      expireFromTimer()
    }, remaining)
  }
  const rearm = () => {
    if (!timer) return
    clearTimer()
    arm()
  }
  const phaseRank: Record<EstablishedAhpResumptionPhase, number> = {
    initial: 0,
    waking: 1,
    connecting: 2,
  }
  if (activity) controller.signal.addEventListener('abort', cleanup, {once: true})

  return {
    get signal() {
      arm()
      return controller.signal
    },
    get phase() {
      return phase
    },
    timeoutMs,
    get expiresAt() {
      return frozenExpiresAt ?? currentExpiresAt()
    },
    get isDisposed() {
      return disposed
    },
    remainingMs,
    advanceTo: nextPhase => {
      arm()
      if (controller.signal.aborted) throw abortReason(controller.signal)
      if (phaseRank[nextPhase] <= phaseRank[phase]) return
      phase = nextPhase
      monotonicExpiresAt = performance.now() + timeoutMs
      if (activity) {
        activeElapsedMs = 0
        activeSince = activity.isActive() ? performance.now() : undefined
        clearTimer()
        arm()
      } else {
        rearm()
      }
    },
    waitFor: async work => {
      arm()
      if (controller.signal.aborted) return Promise.reject(abortReason(controller.signal))

      let onAbort = () => {}
      const aborted = new Promise<never>((_resolve, reject) => {
        onAbort = () => reject(abortReason(controller.signal))
        controller.signal.addEventListener('abort', onAbort, {once: true})
      })
      try {
        const result = await Promise.race([work, aborted])
        if (remainingMs() === 0) {
          if (!controller.signal.aborted) {
            controller.abort(new EstablishedAhpSessionResumptionTimeoutError(timeoutMs))
          }
          throw abortReason(controller.signal)
        }
        return result
      } finally {
        controller.signal.removeEventListener('abort', onAbort)
      }
    },
    cancel: reason => {
      cleanup()
      controller.abort(reason ?? new DOMException('The resumption attempt was cancelled.', 'AbortError'))
    },
    dispose: () => {
      freezeExpiresAt()
      disposed = true
      cleanup()
    },
  }
}
